import { useCallback, useMemo } from "react"
import { Button, Col, Form, FormFeedback, Label, Row } from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"

import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import { useCreateEmployee, useEditEmployee } from "hooks/employeeHook"
import { useShowrooms } from "hooks/showroomHook"

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "on_leave", label: "On Leave" },
  { value: "resigned", label: "Resigned" },
  { value: "terminated", label: "Terminated" },
]

const EMPLOYMENT_TYPE_OPTIONS = [
  { value: "permanent", label: "Permanent" },
  { value: "contractual", label: "Contractual" },
  { value: "probation", label: "Probation" },
  { value: "temporary", label: "Temporary" },
]

const EmployeeAddEdit = ({ modal, toggleModal, isEdit, editedData }) => {
  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateEmployee()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditEmployee()
  const { data: showroomsResponse, isLoading: showroomsLoading } = useShowrooms(
    { all: true, active: true },
    modal
  )

  const showroomOptions = useMemo(
    () =>
      (showroomsResponse?.data ?? []).map(showroom => ({
        value: showroom.id.toString(),
        label: showroom.short_name
          ? `${showroom.name} (${showroom.short_name})`
          : showroom.name,
      })),
    [showroomsResponse]
  )

  const validationSchema = Yup.object({
    showroom_id: Yup.string().required("Showroom is required"),
    name: Yup.string().trim().required("Name is required").max(255),
    mobile: Yup.string().nullable().max(30),
    email: Yup.string().nullable().email("Invalid email").max(255),
    gender: Yup.string().nullable().oneOf(["male", "female", ""]),
    employment_type: Yup.string()
      .nullable()
      .oneOf(["permanent", "contractual", "probation", "temporary", ""]),
    joining_date: Yup.string().required("Joining date is required"),
    status: Yup.string()
      .nullable()
      .oneOf(["active", "inactive", "resigned", "terminated", "on_leave", ""]),
    basic_salary: Yup.number()
      .nullable()
      .transform((v, o) => (o === "" ? null : v))
      .min(0, "Must be positive"),
    gross_salary: Yup.number()
      .nullable()
      .transform((v, o) => (o === "" ? null : v))
      .min(0, "Must be positive"),
    current_address: Yup.string().nullable().max(1000),
    notes: Yup.string().nullable(),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = Object.fromEntries(
        Object.entries(values).filter(
          ([, v]) => v !== "" && v !== null && v !== undefined
        )
      )

      try {
        const action = isEdit
          ? editMutation({ id: editedData.id, ...payload })
          : createMutation(payload)
        const res = await action
        toast.success(
          res?.message ??
            `Employee ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggleModal()
      } catch (error) {
        const msg =
          error?.response?.data?.message ?? "Failed to save employee."
        toast.error(msg)
      }
    },
    [isEdit, editedData, editMutation, createMutation, toggleModal]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      showroom_id: editedData?.showroom_id
        ? String(editedData.showroom_id)
        : "",
      name: editedData?.name || "",
      mobile: editedData?.mobile || "",
      email: editedData?.email || "",
      gender: editedData?.gender || "",
      employment_type: editedData?.employment_type || "probation",
      joining_date: editedData?.joining_date
        ? String(editedData.joining_date).slice(0, 10)
        : "",
      status: editedData?.status || "active",
      basic_salary: editedData?.basic_salary ?? "",
      gross_salary: editedData?.gross_salary ?? "",
      current_address: editedData?.current_address || "",
      notes: editedData?.notes || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const isSubmitting = useMemo(
    () => createLoading || editLoading,
    [createLoading, editLoading]
  )

  const buttonText = isEdit
    ? `Save Changes${isSubmitting ? "..." : ""}`
    : `Add Employee${isSubmitting ? "..." : ""}`

  const renderField = (
    name,
    label,
    type = "text",
    required = false,
    extra = {}
  ) => (
    <div className="mb-3">
      <Label htmlFor={name}>
        {label}
        {required ? <span className="text-danger"> *</span> : null}
      </Label>
      <InputField
        type={type}
        name={name}
        id={name}
        value={formik.values[name]}
        onChange={formik.handleChange}
        invalid={formik.touched[name] && !!formik.errors[name]}
        {...extra}
      />
      {formik.touched[name] && formik.errors[name] && (
        <FormFeedback type="invalid" className="d-block">
          {formik.errors[name]}
        </FormFeedback>
      )}
    </div>
  )

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Employee" : "Add Employee"}
      size="lg"
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        <Row>
          <Col md={6}>
            <div className="mb-3">
              <Label htmlFor="showroom_id">
                Showroom<span className="text-danger"> *</span>
              </Label>
              <InputField
                type="select"
                name="showroom_id"
                id="showroom_id"
                value={formik.values.showroom_id}
                onChange={formik.handleChange}
                invalid={formik.touched.showroom_id && !!formik.errors.showroom_id}
                disabled={showroomsLoading || isSubmitting}
              >
                <option value="">-- Select Showroom --</option>
                {showroomOptions.map(showroom => (
                  <option key={showroom.value} value={showroom.value}>
                    {showroom.label}
                  </option>
                ))}
              </InputField>
              {formik.touched.showroom_id && formik.errors.showroom_id && (
                <FormFeedback type="invalid" className="d-block">
                  {formik.errors.showroom_id}
                </FormFeedback>
              )}
            </div>
          </Col>
          <Col md={6}>{renderField("name", "Full Name", "text", true)}</Col>
        </Row>

        <Row>
          <Col md={6}>{renderField("mobile", "Mobile")}</Col>
          <Col md={6}>{renderField("email", "Email", "email")}</Col>
        </Row>

        <Row>
          <Col md={6}>{renderField("joining_date", "Joining Date", "date", true)}</Col>
        </Row>

        <Row>
          <Col md={4}>
            <div className="mb-3">
              <Label htmlFor="gender">Gender</Label>
              <InputField
                type="select"
                name="gender"
                id="gender"
                value={formik.values.gender}
                onChange={formik.handleChange}
              >
                <option value="">-- Select --</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </InputField>
            </div>
          </Col>
          <Col md={4}>
            <div className="mb-3">
              <Label htmlFor="employment_type">Employment Type</Label>
              <InputField
                type="select"
                name="employment_type"
                id="employment_type"
                value={formik.values.employment_type}
                onChange={formik.handleChange}
              >
                {EMPLOYMENT_TYPE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </InputField>
            </div>
          </Col>
          <Col md={4}>
            <div className="mb-3">
              <Label htmlFor="status">Status</Label>
              <InputField
                type="select"
                name="status"
                id="status"
                value={formik.values.status}
                onChange={formik.handleChange}
              >
                {STATUS_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </InputField>
            </div>
          </Col>
        </Row>

        <Row>
          <Col md={6}>{renderField("basic_salary", "Basic Salary", "number")}</Col>
          <Col md={6}>{renderField("gross_salary", "Gross Salary", "number")}</Col>
        </Row>

        {renderField("current_address", "Current Address", "textarea")}
        {renderField("notes", "Notes", "textarea")}

        <div className="text-end mt-3">
          <Button color="success" type="submit" disabled={isSubmitting}>
            {buttonText}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default EmployeeAddEdit

